/**
 * The Leadership Brief tab's switch and settings (GUS's Atlas "executive" view,
 * `features.modules.atlas.tabs.executive`).
 *
 * Every control writes under that one node, through the same
 * `onChange(path, value)` the flag tree uses. A cleared field removes its key,
 * so absent always means GUS's default. A stored boolean node (`executive: true`)
 * is turned into `{"enabled": true}` before anything is added beside it, so the
 * switch is never lost. What is wrong with a value comes from
 * executiveSettings.ts, the same check the backend runs before it saves.
 */
import React from 'react';
import { Toggle } from '../../design-system/components/Toggle';
import { setPath } from '../flags/flagPaths';
import { Bilingual, SettingError } from '../satisfaction/satisfactionSettings';
import {
  DEFAULT_FIXED_BASELINES, DEFAULT_LABEL, EXECUTIVE_CONFIG, EXECUTIVE_PATH, aiWrittenOn, executiveErrors,
  executiveNode, executiveTabOn,
} from './executiveSettings';
import StartingSetsEditor from './StartingSetsEditor';
import {
  Arabic, FieldLabel, Grid, Help, IconButton, Input, Key, Note, NumberInput, Panel, Row, Setting, SmallButton, State,
  Title, TitleRow,
} from './styles';

interface Props {
  projectId?: string;
  storedFlags: Record<string, any>;
  onChange: (path: string, value: any) => void;
  disabled?: boolean;
  /**
   * What the tenant's own flag manifest says an absent switch means: true when
   * that GUS build shows the tab by default, false when it agrees it is off,
   * null when its manifest does not describe the tab, and undefined when the
   * manifest was not read at all.
   */
  manifestDefaultOn?: boolean | null;
}

const isPlain = (v: unknown): v is Record<string, any> => v !== null && typeof v === 'object' && !Array.isArray(v);
const present = (v: unknown) => v !== undefined && v !== null;

/** Drop containers a cleared field leaves empty, so "nothing set" is stored as nothing. */
function tidy(node: Record<string, any>): Record<string, any> {
  const cfg = node.config;
  if (isPlain(cfg)) {
    for (const key of ['starting_sets', 'deck_project_name']) {
      if (isPlain(cfg[key]) && Object.values(cfg[key]).every((v) => v === undefined)) delete cfg[key];
    }
    if (Object.values(cfg).every((v) => v === undefined)) delete node.config;
  }
  return node;
}

/** Digits (Arabic-Indic too) become a number, empty is "not set", anything else stays text so the check shows it. */
const parseWhole = (raw: string): number | string | undefined => {
  const t = raw.trim().replace(/[٠-٩۰-۹]/g, (d) => String(d.charCodeAt(0) & 0xf));
  if (t === '') return undefined;
  return /^-?[0-9]+$/.test(t) ? Number(t) : raw;
};

const Both: React.FC<Bilingual> = ({ en, ar }) => (
  <>
    {en} <span lang="ar" dir="rtl">/ {ar}</span>
  </>
);

const Errors: React.FC<{ errors: SettingError[] }> = ({ errors }) => (
  <>
    {errors.map((e) => (
      <Note key={`${e.rule}-${e.path}`} $tone="error" role="alert"><Both {...e.message} /></Note>
    ))}
  </>
);

const Heading: React.FC<{ en: string; ar: string; k: string; state?: Bilingual }> = ({ en, ar, k, state }) => (
  <TitleRow>
    <Title>{en}</Title>
    <Arabic lang="ar" dir="rtl">{ar}</Arabic>
    <Key>{k}</Key>
    {state && <State><Both {...state} /></State>}
  </TitleRow>
);

const LeadershipBriefPanel: React.FC<Props> = ({
  projectId, storedFlags, onChange, disabled = false, manifestDefaultOn,
}) => {
  const node = executiveNode(storedFlags);
  const obj: Record<string, any> = isPlain(node) ? node : {};
  const cfg: Record<string, any> = isPlain(obj.config) ? obj.config : {};
  const errors = executiveErrors(storedFlags);
  const errorsAt = (sub: string) => {
    const path = `${EXECUTIVE_PATH}.${sub}`;
    return errors.filter((e) => e.path === path || e.path.startsWith(`${path}.`));
  };

  // Everything is written as the whole tab node, so a boolean switch becomes {"enabled": <it>} first.
  const write = (sub: string, value: unknown) => {
    const base = isPlain(node) ? node : typeof node === 'boolean' ? { enabled: node } : {};
    onChange(EXECUTIVE_PATH, tidy(setPath(base, sub, value)));
  };

  const on = executiveTabOn(storedFlags);
  const enabledSet = typeof node === 'boolean' || present(obj.enabled);
  const ai = aiWrittenOn(storedFlags);
  const fixed: unknown = cfg.fixed_baselines;
  const fixedList: string[] = Array.isArray(fixed) ? fixed.filter((x): x is string => typeof x === 'string') : [];
  const deckName: Record<string, unknown> = isPlain(cfg.deck_project_name) ? cfg.deck_project_name : {};
  const [prefixDraft, setPrefixDraft] = React.useState('');

  const notSet: Bilingual = { en: 'not set: GUS default', ar: 'غير مضبوط: الإعداد الافتراضي في GUS' };

  return (
    <Panel aria-label="Leadership Brief">
      <Errors errors={errors.filter((e) => e.path === EXECUTIVE_PATH || e.path === EXECUTIVE_CONFIG)} />

      <Setting $error={errorsAt('enabled').length > 0}>
        <Heading
          en="Show the Leadership Brief tab"
          ar="إظهار تبويب الموجز القيادي"
          k="atlas.executive"
          state={enabledSet ? undefined : { en: 'not set: off', ar: 'غير مضبوط: معطّل' }}
        />
        <Row>
          <Toggle
            checked={on}
            onChange={(checked) => write('enabled', checked)}
            disabled={disabled}
            size="sm"
            label="Show the Leadership Brief tab"
          />
          <State><Both {...(on ? { en: 'On', ar: 'مفعّل' } : { en: 'Off', ar: 'معطّل' })} /></State>
        </Row>
        <Help>
          <Both
            en="Off by default: the tab shows only where an admin turns it on. Off hides the tab, and the “Open in Leadership Brief” link on every dashboard."
            ar="معطّل افتراضيًا: لا يظهر التبويب إلا حيث يفعّله أحد المشرفين. التعطيل يخفي التبويب ورابط «افتح في الموجز القيادي» في كل لوحة معلومات."
          />
        </Help>
        {manifestDefaultOn === true && !enabledSet && (
          <Note $tone="warning" role="alert">
            <Both
              en="This project's GUS build still shows the tab when the switch is not set. To keep it off on this build, turn the switch on and off again so “off” is saved."
              ar="ما زال إصدار GUS لهذا المشروع يُظهر التبويب حين لا يكون المفتاح مضبوطًا. لإبقائه معطّلًا على هذا الإصدار، فعّل المفتاح ثم عطّله ليُحفظ «معطّل»."
            />
          </Note>
        )}
        {manifestDefaultOn === undefined && (
          <Note $tone="info">
            <Both
              en="Not checked against this project's GUS build: its flag list could not be read, so the portal cannot confirm that this build also reads an unset switch as off."
              ar="لم يُتحقق من ذلك مقابل إصدار GUS لهذا المشروع: تعذّرت قراءة قائمة مفاتيحه، لذا لا تستطيع البوابة التأكد من أن هذا الإصدار يقرأ المفتاح غير المضبوط على أنه معطّل أيضًا."
            />
          </Note>
        )}
        {manifestDefaultOn === null && (
          <Note $tone="info">
            <Both
              en="This project's GUS does not list the Leadership Brief yet, so the tab cannot show there until GUS is updated."
              ar="لا يدرج GUS الخاص بهذا المشروع الموجز القيادي بعد، لذا لا يمكن أن يظهر التبويب هناك حتى يُحدَّث GUS."
            />
          </Note>
        )}
        <Errors errors={errorsAt('enabled')} />
      </Setting>

      <Setting $error={errorsAt('order').length > 0}>
        <Heading en="Position" ar="الترتيب" k="order" state={present(obj.order) ? undefined : notSet} />
        <Row>
          <NumberInput
            type="text"
            inputMode="numeric"
            aria-label="Position"
            value={present(obj.order) ? String(obj.order) : ''}
            disabled={disabled}
            onChange={(e) => write('order', parseWhole(e.target.value))}
          />
        </Row>
        <Help>
          <Both
            en="From its Leadership Brief release, GUS puts the tab first in the Atlas tab row when it is on (earlier builds put it last). A number places it among the tabs that have one, lowest first."
            ar="ابتداءً من إصدار الموجز القيادي، يضع GUS التبويب أولًا في صف تبويبات الأطلس عند تفعيله (الإصدارات الأقدم تضعه أخيرًا). الرقم يضعه بين التبويبات التي لها رقم، الأصغر أولًا."
          />
        </Help>
        <Errors errors={errorsAt('order')} />
      </Setting>

      <Setting $error={errorsAt('label').length + errorsAt('label_ar').length > 0}>
        <Heading en="Tab label" ar="تسمية التبويب" k="label / label_ar" />
        <Grid>
          <FieldLabel>
            <span>English</span>
            <Input
              value={typeof obj.label === 'string' ? obj.label : ''}
              placeholder={DEFAULT_LABEL.en}
              disabled={disabled}
              onChange={(e) => write('label', e.target.value === '' ? undefined : e.target.value)}
            />
          </FieldLabel>
          <FieldLabel>
            <span>العربية</span>
            <Input
              lang="ar"
              dir="rtl"
              value={typeof obj.label_ar === 'string' ? obj.label_ar : ''}
              placeholder={DEFAULT_LABEL.ar}
              disabled={disabled}
              onChange={(e) => write('label_ar', e.target.value === '' ? undefined : e.target.value)}
            />
          </FieldLabel>
        </Grid>
        <Help>
          <Both
            en={`Empty means the default label: “${DEFAULT_LABEL.en}” and “${DEFAULT_LABEL.ar}”. The key GUS reads stays “executive”.`}
            ar={`الحقل الفارغ يعني التسمية الافتراضية: «${DEFAULT_LABEL.en}» و«${DEFAULT_LABEL.ar}». يبقى المفتاح الذي يقرؤه GUS «executive».`}
          />
        </Help>
        <Errors errors={[...errorsAt('label'), ...errorsAt('label_ar')]} />
      </Setting>

      <Setting $error={errorsAt('ai_written').length > 0}>
        <Heading
          en="AI-written sentences"
          ar="جمل يكتبها الذكاء الاصطناعي"
          k="atlas.executive.ai_written"
          state={present(obj.ai_written) ? undefined : { en: 'not set: on', ar: 'غير مضبوط: مفعّل' }}
        />
        <Row>
          <Toggle
            checked={ai}
            onChange={(checked) => write('ai_written', checked)}
            disabled={disabled}
            size="sm"
            label="AI-written sentences"
          />
          <State><Both {...(ai ? { en: 'On', ar: 'مفعّل' } : { en: 'Off', ar: 'معطّل' })} /></State>
          {present(obj.ai_written) && (
            <IconButton type="button" disabled={disabled} onClick={() => write('ai_written', undefined)}>
              Use GUS default / استخدم الإعداد الافتراضي في GUS
            </IconButton>
          )}
        </Row>
        <Help>
          <Both
            en="Whether the tab and its report may carry sentences the AI writes. Off: template text only, made from the same figures, with no model call. The figures never change either way."
            ar="هل يجوز أن يحمل التبويب وتقريره جملًا يكتبها الذكاء الاصطناعي. عند التعطيل: نص قالب فقط، مبني على الأرقام نفسها، دون أي استدعاء للنموذج. لا تتغير الأرقام في الحالتين."
          />
        </Help>
        <Errors errors={errorsAt('ai_written')} />
      </Setting>

      <Setting $error={errorsAt('config.starting_sets').length > 0}>
        <Heading
          en="Starting KPIs"
          ar="المؤشرات الأولى"
          k="atlas.executive.config.starting_sets"
        />
        <Help>
          <Both
            en="The KPIs each dashboard offers before anyone picks, and each person’s starting “My KPIs” list. A list set here replaces GUS’s default for that dashboard. An item this project cannot serve is dropped by GUS, so it is marked here."
            ar="المؤشرات التي تعرضها كل لوحة معلومات قبل أن يختار أحد، وقائمة «مؤشراتي» الأولى لكل شخص. القائمة المضبوطة هنا تحل محل الإعداد الافتراضي في GUS لتلك اللوحة. العنصر الذي لا يستطيع هذا المشروع تقديمه يُسقطه GUS، لذا يُعلَّم هنا."
          />
        </Help>
        {present(cfg.starting_sets) && !isPlain(cfg.starting_sets) ? (
          <Row>
            <Errors errors={errorsAt('config.starting_sets')} />
            <SmallButton type="button" disabled={disabled} onClick={() => write('config.starting_sets', undefined)}>
              Use GUS default / استخدم الإعداد الافتراضي في GUS
            </SmallButton>
          </Row>
        ) : (
          <StartingSetsEditor
            projectId={projectId}
            sets={cfg.starting_sets}
            errors={errorsAt('config.starting_sets')}
            disabled={disabled}
            onSet={(dashboard, list) => write(`config.starting_sets.${dashboard}`, list)}
            onRemoveKey={(key) => write(`config.starting_sets.${key}`, undefined)}
          />
        )}
      </Setting>

      <Setting $error={errorsAt('config.deck_project_name').length > 0}>
        <Heading
          en="Project name in the report"
          ar="اسم المشروع في التقرير"
          k="atlas.executive.config.deck_project_name"
          state={present(cfg.deck_project_name) ? undefined : { en: 'not set: the place’s own name', ar: 'غير مضبوط: اسم المكان نفسه' }}
        />
        {present(cfg.deck_project_name) && !isPlain(cfg.deck_project_name) ? (
          <Row>
            <Errors errors={errorsAt('config.deck_project_name')} />
            <SmallButton type="button" disabled={disabled} onClick={() => write('config.deck_project_name', undefined)}>
              Use GUS default / استخدم الإعداد الافتراضي في GUS
            </SmallButton>
          </Row>
        ) : (
          <>
            <Grid>
              <FieldLabel>
                <span>English</span>
                <Input
                  value={typeof deckName.en === 'string' ? deckName.en : ''}
                  placeholder="The place’s own name"
                  disabled={disabled}
                  onChange={(e) => write('config.deck_project_name.en', e.target.value === '' ? undefined : e.target.value)}
                />
              </FieldLabel>
              <FieldLabel>
                <span>العربية</span>
                <Input
                  lang="ar"
                  dir="rtl"
                  value={typeof deckName.ar === 'string' ? deckName.ar : ''}
                  placeholder="اسم المكان نفسه"
                  disabled={disabled}
                  onChange={(e) => write('config.deck_project_name.ar', e.target.value === '' ? undefined : e.target.value)}
                />
              </FieldLabel>
            </Grid>
            {present(deckName.en) !== present(deckName.ar) && (
              <Note $tone="warning">
                <Both
                  en={`Only the ${present(deckName.en) ? 'English' : 'Arabic'} name is set: the ${present(deckName.en) ? 'Arabic' : 'English'} report prints the place’s own name.`}
                  ar={`الاسم ${present(deckName.en) ? 'الإنجليزي' : 'العربي'} وحده مضبوط: يطبع التقرير ${present(deckName.en) ? 'العربي' : 'الإنجليزي'} اسم المكان نفسه.`}
                />
              </Note>
            )}
            <Help>
              <Both
                en="Empty means the place’s own name. The Arabic report prints the city’s Arabic name, CITY_NAME_AR, which is shown (read-only) on the Tenant Config tab."
                ar="الحقل الفارغ يعني اسم المكان نفسه. يطبع التقرير العربي اسم المدينة بالعربية، CITY_NAME_AR، المعروض (للقراءة فقط) في تبويب إعدادات المستأجر."
              />
            </Help>
            <Errors errors={errorsAt('config.deck_project_name')} />
          </>
        )}
      </Setting>

      <details>
        <summary>
          <Both en="Advanced" ar="خيارات متقدمة" />
        </summary>
        <Setting $error={errorsAt('config.fixed_baselines').length > 0} style={{ marginTop: 12 }}>
          <Heading
            en="Fixed baselines"
            ar="خطوط أساس ثابتة"
            k="atlas.executive.config.fixed_baselines"
            state={present(fixed) ? undefined : notSet}
          />
          <Help>
            <Both
              en="Measure-id prefixes whose figures come from a fixed snapshot rather than a monthly series. The page words them by what is held: a single reading is a fixed baseline, and a later release reads as a change between the months it was read."
              ar="بادئات معرّفات المقاييس التي تأتي أرقامها من لقطة ثابتة لا من سلسلة شهرية. تصفها الصفحة بحسب المتوفر: القراءة الوحيدة خط أساس ثابت، والإصدار اللاحق يُقرأ تغيّرًا بين الشهرين اللذين قُرئ فيهما."
            />
          </Help>
          {!present(fixed) ? (
            <>
              <Row>
                {DEFAULT_FIXED_BASELINES.map((p) => <Key key={p}>{p}</Key>)}
              </Row>
              <Row>
                <SmallButton
                  type="button"
                  disabled={disabled}
                  onClick={() => write('config.fixed_baselines', [...DEFAULT_FIXED_BASELINES])}
                >
                  Change this list / غيّر هذه القائمة
                </SmallButton>
              </Row>
            </>
          ) : Array.isArray(fixed) ? (
            <>
              <Row>
                {fixedList.map((p, i) => (
                  <Key key={`${p}-${i}`}>
                    {p}{' '}
                    <IconButton
                      type="button"
                      aria-label={`Remove ${p}`}
                      disabled={disabled}
                      onClick={() => {
                        const next = fixedList.filter((_, j) => j !== i);
                        write('config.fixed_baselines', next.length ? next : undefined);
                      }}
                    >
                      ✕
                    </IconButton>
                  </Key>
                ))}
              </Row>
              <Row>
                <Input
                  style={{ maxWidth: 280 }}
                  aria-label="Add a prefix"
                  placeholder="surface:snapshot."
                  value={prefixDraft}
                  disabled={disabled}
                  onChange={(e) => setPrefixDraft(e.target.value)}
                />
                <SmallButton
                  type="button"
                  disabled={disabled || prefixDraft.trim() === '' || fixedList.includes(prefixDraft.trim())}
                  onClick={() => {
                    write('config.fixed_baselines', [...fixedList, prefixDraft.trim()]);
                    setPrefixDraft('');
                  }}
                >
                  Add / أضف
                </SmallButton>
                <SmallButton type="button" disabled={disabled} onClick={() => write('config.fixed_baselines', undefined)}>
                  Use GUS default / استخدم الإعداد الافتراضي في GUS
                </SmallButton>
              </Row>
            </>
          ) : (
            <Row>
              <SmallButton type="button" disabled={disabled} onClick={() => write('config.fixed_baselines', undefined)}>
                Use GUS default / استخدم الإعداد الافتراضي في GUS
              </SmallButton>
            </Row>
          )}
          <Errors errors={errorsAt('config.fixed_baselines')} />
        </Setting>
      </details>

      <Help>
        <Both
          en="The readings schedule, the report’s limits and the “Save this month’s readings now” button are on the Tenant Config tab."
          ar="جدول القراءات وحدود التقرير وزر «احفظ قراءات هذا الشهر الآن» في تبويب إعدادات المستأجر."
        />
      </Help>
    </Panel>
  );
};

export default LeadershipBriefPanel;
